import React, { useEffect, useState } from 'react'

import { Modal, Box, TextField, Autocomplete } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { handleNumber, deleteZero, handleZero, isValid } from '../../helpers/helper'

const style = {
    minWidth: "360px",
    maxWidth: "540px",
    width: "50%",
    height: "auto",
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: '#ffffff',
    border: 'transparent',
    borderRadius: '5px',
    boxShadow: 24,
    zIndex: "1600",
    outline: 'none'
}

const COST_TEXTFIELD_STYLE = {
    width: '100%',
    background: '#FCFCFC',
    '.MuiOutlinedInput-input': {
        padding: '9px !important'
    },
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': {
        border: '1px solid #EEEFF2 !important'
    }
}

const AddRoutingOperation = ({ open, handleOpen, operations, setOperations, editingOperation = null, workCenters = [], operationOptions = [], rateUomOptions = [] }) => {

    const { t } = useTranslation()

    /* form */
    const [operation, setOperation] = useState('')
    const [workCenterId, setWorkCenterId] = useState('')
    const [workCenterName, setWorkCenterName] = useState('')
    const [productionRate, setProductionRate] = useState('')
    const [rateUom, setRateUom] = useState('')
    const [setupTime, setSetupTime] = useState('')

    const isFormValid = () => (
        isValid(operation)
        && isValid(workCenterId)
        && productionRate !== ''
        && isValid(rateUom)
        && setupTime !== ''
    )

    useEffect(() => {
        if (open && editingOperation) {
            setOperation(editingOperation.operation ?? '')
            setWorkCenterId(editingOperation.workCenterId ?? '')
            setWorkCenterName(editingOperation.workCenterName || workCenters.find(wc => wc.id === editingOperation.workCenterId)?.work_center_name || '')
            setProductionRate(editingOperation.productionRate ?? '')
            setRateUom(editingOperation.rateUom ?? '')
            setSetupTime(editingOperation.setupTime ?? '')
        }
    }, [open, editingOperation, workCenters])

    const closeAndCleanUp = () => {
        handleOpen()
        setOperation('')
        setWorkCenterId('')
        setWorkCenterName('')
        setProductionRate('')
        setRateUom('')
        setSetupTime('')
    }

    const handleWorkCenterChange = (a, b) => {
        setWorkCenterId(b?.id || '')
        setWorkCenterName(b?.label || '')
    }

    const handleOperationChange = (a, b) => {
        setOperation(b?.id || '')
    }

    const handleRateUomChange = (a, b) => {
        setRateUom(b?.id || '')
    }

    const saveOperation = (e) => {
        e.preventDefault()

        const operation_payload = {
            operation,
            workCenterId,
            workCenterName,
            productionRate: Number(productionRate),
            rateUom,
            setupTime: setupTime 
        }

        if (editingOperation) {
            setOperations((prev) => prev.map((row) => row.id === editingOperation.id ? { ...row, ...operation_payload } : row))
        } else {
            const nextSequence = operations.length > 0 ? Math.max(...operations.map(row => row.sequence)) + 10 : 10
            setOperations((prev) => [...prev, { ...operation_payload, id: Date.now(), sequence: nextSequence }])
        }

        closeAndCleanUp()
    }

    const optionsWorkCenters = workCenters?.map(option => ({ label: option.work_center_name, id: option.id }))
    const optionsOperations = operationOptions?.map(option => ({ label: option.label, id: option.value }))
    const optionsRateUom = rateUomOptions?.map(option => ({ label: option.label, id: option.value }))

    return (
        <Modal open={open} onClose={closeAndCleanUp}>
            <Box sx={style}>
                <div className='flex justify-between items-center p-5 w-full' style={{ backgroundColor: '#336195', borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-baseline justify-between w-full'>
                        <p className='text-[18px] font-[600] text-white'>{editingOperation ? 'Edit Operation' : 'Add Operation'}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-white">
                            <button onClick={closeAndCleanUp}><i className="fa-solid fa-plus"></i></button>
                        </div>
                    </div>
                </div>
                <div>
                    <div className='p-5' style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                        <form className='flex justify-center flex-col items-start pt-1'>
                            {/* Operation */}
                            <div className='pb-4 w-full'>
                                <p className="text-[14px] text-[#718096]">Operation *</p>
                                <Autocomplete
                                    disablePortal
                                    id="operation-name-autocomplete"
                                    options={optionsOperations}
                                    value={operation ? { label: optionsOperations.find(o => o.id === operation)?.label ?? '', id: operation } : null}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    sx={{
                                        width: '100%',
                                        background: '#FCFCFC',
                                        '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none',
                                        '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    onChange={handleOperationChange}
                                    renderInput={(params) => <TextField {...params} placeholder="Select Operation" />}
                                />
                            </div>

                            {/* Work Center */}
                            <div className='pb-4 w-full'>
                                <p className="text-[14px] text-[#718096]">Work Center *</p>
                                <Autocomplete
                                    disablePortal
                                    id="operation-work-center-autocomplete"
                                    options={optionsWorkCenters}
                                    value={workCenterId ? { label: workCenterName, id: workCenterId } : null}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    sx={{
                                        width: '100%',
                                        background: '#FCFCFC',
                                        '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none',
                                        '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    onChange={handleWorkCenterChange}
                                    renderInput={(params) => <TextField {...params} placeholder="Select Work Center" />}
                                />
                            </div>

                            <div className='flex gap-5 w-full pb-4'>
                                {/* Production Rate */}
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">Production Rate *</p>
                                    <TextField
                                        sx={COST_TEXTFIELD_STYLE}
                                        type="text"
                                        value={productionRate}
                                        onChange={(e) => handleNumber({ event: e, setter: setProductionRate, precision: 2 })}
                                        onBlur={e => handleZero({ event: e, setter: setProductionRate })}
                                        onFocus={e => deleteZero({ event: e, setter: setProductionRate, value: productionRate })}
                                    />
                                </div>

                                {/* Rate UOM */}
                                <div className='w-full'>
                                    <p className="text-[14px] text-[#718096]">Rate UOM *</p>
                                    <Autocomplete
                                        disablePortal
                                        id="operation-rate-uom-autocomplete"
                                        options={optionsRateUom}
                                        value={rateUom ? { label: optionsRateUom.find(o => o.id === rateUom)?.label ?? '', id: rateUom } : null}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        sx={{
                                            width: '100%',
                                            background: '#FCFCFC',
                                            '.MuiOutlinedInput-input': { padding: '0 !important' },
                                            boxShadow: 'none',
                                            '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                        onChange={handleRateUomChange}
                                        renderInput={(params) => <TextField {...params} placeholder="Select Rate UOM" />}
                                    />
                                </div>
                            </div>

                            {/* Setup Time */}
                            <div className='pb-5 w-full'>
                                <p className="text-[14px] text-[#718096]">Setup Time (Minutes) *</p>
                                <TextField
                                    sx={COST_TEXTFIELD_STYLE}
                                    type="text"
                                    value={setupTime}
                                    onChange={(e) => handleNumber({ event: e, setter: setSetupTime, precision: 2 })}
                                    onBlur={e => handleZero({ event: e, setter: setSetupTime })}
                                    onFocus={e => deleteZero({ event: e, setter: setSetupTime, value: setupTime })}
                                />
                            </div>
                        </form>
                        <div className='w-full flex gap-2 justify-between'>
                            <button type="button" onClick={closeAndCleanUp} className='w-full text-[14px] text-[#919399] rounded-sm px-4 py-2 bg-[#F3F5F7] hover:bg-[#E6E6E6] disabled:bg-[#B8B7BC] capitalize'>
                                {t('cancel')}
                            </button>
                            <button type="button" onClick={saveOperation} disabled={!isFormValid()} className='w-full text-[14px] text-white rounded-sm px-4 py-2 bg-[#015D9F] hover:bg-[#003459] disabled:bg-[#B8B7BC] capitalize'>
                                {editingOperation ? t('save') : t('add')}
                            </button>
                        </div>
                    </div>
                </div>
            </Box>
        </Modal>
    )
}

export default AddRoutingOperation
